import React, {useEffect} from 'react';
import {isDesktop} from 'react-device-detect';
import {FlatList, ListRenderItem, View} from 'react-native';
import {observer} from 'mobx-react';

import {DEFAULT_CAROUSEL_NUM_ITEMS} from '../../constants/GlobalConstants';
import LastMinuteOptionTileCard from '../../screens/cart/components/LastMinuteOptionTileCard';
import {LinesParams} from '../../stores/linesStore';
import {useStores} from '../../stores/use-stores';
import {OrderLine} from '../../transport/apiServiceResponse';
import { getProductDisplayRules } from '../../utils/rewards';
import TitleLine from '../rewards/TitleLine';
import ActivityIndicator from '../themed/ActivityIndicator';
import Text from '../themed/Text';

export type HasCartScreen = {
  /**
   * element is on cart screen so should exclude cart items
   */
  cartScreen?: boolean;
};

type MemberPriceCarouselProps = Omit<
  ProductCarouselProps,
  'data' | 'isLoading'
> &
  Required<Pick<LinesParams, 'member_price'>> &
  HasCartScreen;

const MemberPriceCarousel = observer(
  ({
    title,
    onSeeAllPress,
    member_price,
    cartScreen,
  }: MemberPriceCarouselProps) => {
    const {cartStore, getLinesStore} = useStores();
    const store = getLinesStore({member_price});

    const excludeProductIds = cartScreen
      ? (cartStore.currentOrder?.order_lines || [])?.map(
          ({product_id}: any) => product_id,
        )
      : [];

    const data = (store?.lines || []).filter((product) => {
      const {product_id} = product;
      const isNotAlreadyInCart = !excludeProductIds.includes(product_id);
      const displayRules = getProductDisplayRules(product);
      const showProduct = member_price === 'public' ? displayRules.presentAsPublicMemberProduct : 
        member_price === 'private' ? displayRules.presentAsPrivateMemberProduct : true;
      
      return showProduct && isNotAlreadyInCart;
    });

    return (
      <ProductCarousel
        title={title}
        onSeeAllPress={onSeeAllPress}
        data={data}
        isLoading={store?.isLoading}
      />
    );
  },
);

const LastMinuteOptionsCarousel = observer(() => {
  const {cartStore} = useStores();
  const orderLinesIds =
    cartStore.currentOrder?.order_lines?.map((item: any) => item.product_id) ||
    [];
  const lastMinuteOptionsItems = (
    cartStore.promotedAtCheckoutProducts || []
  ).filter((item: any) => !orderLinesIds.includes(item.product_id));

  useEffect(() => {
    cartStore.getProductsPromotedAtCheckout();
  }, [cartStore]);

  return (
    <ProductCarousel
      title="LAST MINUTE OPTIONS"
      data={lastMinuteOptionsItems}
    />
  );
});

type ProductCarouselProps = {
  title?: string;

  /**
   * Shows see all button if this is set
   */
  onSeeAllPress?: any;
  isLoading?: boolean;
  data?: any[];
  numItems?: number;
};

function ProductCarousel({
  title,
  onSeeAllPress,
  isLoading,
  data,
  numItems = DEFAULT_CAROUSEL_NUM_ITEMS,
}: ProductCarouselProps) {
  return (
    <View testID="product-carousel">
      {!!title && <TitleLine title={title} onSeeAllPress={onSeeAllPress} />}
      {data?.length === 0 && !isLoading ? (
        <Empty />
      ) : (
        <FlatList
          contentContainerStyle={{paddingBottom: 1}}
          data={data?.slice(0, numItems)}
          renderItem={renderItem}
          ItemSeparatorComponent={Spacer}
          horizontal
          showsHorizontalScrollIndicator={isDesktop}
          ListEmptyComponent={ActivityIndicator}
        />
      )}
    </View>
  );
}

const Empty = () => {
  return <Text>No products found.</Text>;
};

const renderItem: ListRenderItem<OrderLine> = ({item, index}) => {
  return (
    <LastMinuteOptionTileCard
      item={item}
      index={index}
      key={item.product_id}
      isTotalVisible={false}
    />
  );
};

function Spacer() {
  return <View style={{height: 8, width: 8}} />;
}

export {ProductCarousel, MemberPriceCarousel, LastMinuteOptionsCarousel};
